import type {
    CircleSpawnPattern,
    ConcentricRingsEffect,
    ConnectionPreset,
    ConnectSelectionOptions,
    DiagramArc,
    DiagramDocument,
    DiagramEllipse,
    DiagramHeatZone,
    DiagramLayer,
    DiagramLine,
    DiagramNode,
    DiagramPattern,
    DiagramPolygon,
    DiagramRect,
    Effect,
    EffectKind,
    GroupOutlinePreset,
    MegaNodeBrushOptions,
    MultiNodeBrushOptions,
    NodeBrushLayout,
    NodeBrushOptions,
    NodeInteractOptions,
    NodeGroup,
    OutlineProperty,
    RadialEmitterEffect,
    ResolvedConnectionStyle,
    SpawnPattern,
    SpawnPatternKind,
    SquareSpawnPattern,
    StrokeStyle,
    Vec2,
} from "./types";
import {
    newId,
} from "./types";
import { SYMMETRY_MATCH_TOLERANCE } from "./symmetry";

export function createLayer(index: number): DiagramLayer {
    return {
        id: newId("layer"),
        name: `Layer ${index + 1}`,
        visible: true,
        locked: false,
    };
}

// --- factories ------------------------------------------------------------

export const DEFAULT_NODE_RADIUS = 10;

export function createNode(
    position: Vec2,
    index: number,
    layerId: string,
): DiagramNode {
    return {
        id: newId("node"),
        label: `Node ${index + 1}`,
        layerId,
        position: { ...position },
        radius: DEFAULT_NODE_RADIUS,
        color: "#7ca7ff",
        shape: "circle",
        visible: true,
        effectIds: [],
        heatReactive: false,
        spawn: null,
        spawnedFrom: null,
    };
}

/** Default parameters for the node-brush composite subtool. */
export function createNodeBrushOptions(): NodeBrushOptions {
    return {
        radius: 80,
        count: 5,
        layout: "sunflower",
        connect: true,
        connectMode: "sequence",
        group: false,
    };
}

/** Default parameters for the Connect-selection subtool. */
export function createConnectSelectionOptions(): ConnectSelectionOptions {
    return {
        mode: "complete",
        minDistanceEnabled: false,
        minDistance: 0,
        maxDistanceEnabled: false,
        maxDistance: 200,
    };
}

/** Default parameters for the node-interact subtool. */
export function createNodeInteractOptions(): NodeInteractOptions {
    return {
        action: "attract",
        radius: 240,
        strength: 0.3,
        falloff: "smooth",
        connectMode: "direct",
        symmetryTolerance: SYMMETRY_MATCH_TOLERANCE,
    };
}

/** Default parameters for the multi-node-brush composite subtool. */
export function createMultiNodeBrushOptions(): MultiNodeBrushOptions {
    return {
        spreadRadius: 220,
        count: 4,
        layout: "sunflower",
        groupAll: false,
    };
}

/** Default parameters for the mega-node-brush composite subtool. */
export function createMegaNodeBrushOptions(): MegaNodeBrushOptions {
    return {
        mode: "scatter",
        count: 3,
        groupAll: false,
        spreadRadius: 600,
        layout: "sunflower",
        radiusStep: 120,
        countStep: 2,
    };
}

/**
 * The node positions a single node-brush stroke drops, given its `center`,
 * `radius`, `count`, and `layout`. Pure — returns exactly `count` points
 * (deterministic for every layout; the `random` layout is seeded by `seed` so a
 * given stroke is reproducible). A single point always lands dead center.
 *
 * Shared by the brush action and (potentially) any preview; kept out of the
 * React context so it can be unit-reasoned about in isolation.
 */
export function nodeBrushPositions(
    center: Vec2,
    radius: number,
    count: number,
    layout: NodeBrushLayout,
    seed = 1,
): Vec2[] {
    const n = Math.max(1, Math.floor(count));
    const R = Math.max(0, radius);
    if (n === 1) return [{ ...center }];
    const at = (r: number, a: number): Vec2 => ({
        x: center.x + r * Math.cos(a),
        y: center.y + r * Math.sin(a),
    });
    const out: Vec2[] = [];

    switch (layout) {
        case "random": {
            // mulberry32 PRNG seeded per stroke; sqrt on the radius keeps the
            // scatter uniform over area rather than clustered at the center.
            let t = (seed >>> 0) || 1;
            const rand = () => {
                t += 0x6d2b79f5;
                let x = t;
                x = Math.imul(x ^ (x >>> 15), x | 1);
                x ^= x + Math.imul(x ^ (x >>> 7), x | 61);
                return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
            };
            for (let i = 0; i < n; i += 1) {
                out.push(at(R * Math.sqrt(rand()), rand() * Math.PI * 2));
            }
            break;
        }
        case "ring": {
            // Evenly spaced points on the radius circle.
            for (let i = 0; i < n; i += 1) {
                out.push(at(R, (i / n) * Math.PI * 2));
            }
            break;
        }
        case "grid": {
            // Square grid sized to hold n points, clipped to the disk. Walk the
            // grid centered on the press point, keeping cells within the radius
            // until n points are collected; fall back to the closest cells if
            // the disk doesn't yield enough (degenerate tiny radius).
            const cols = Math.ceil(Math.sqrt(n));
            const step = cols > 1 ? (2 * R) / (cols - 1) : 0;
            type Cell = { p: Vec2; d: number };
            const cells: Cell[] = [];
            for (let r = 0; r < cols; r += 1) {
                for (let c = 0; c < cols; c += 1) {
                    const x = center.x - R + c * step;
                    const y = center.y - R + r * step;
                    const d = Math.hypot(x - center.x, y - center.y);
                    cells.push({ p: { x, y }, d });
                }
            }
            // Prefer in-disk cells, ordered by distance from center, so the
            // grid reads as a centered cluster.
            cells.sort((a, b) => a.d - b.d);
            for (const cell of cells) {
                if (out.length >= n) break;
                out.push(cell.p);
            }
            break;
        }
        case "spiral": {
            // Archimedean spiral: radius grows linearly with the turn so the
            // arms stay evenly spaced, winding out to R over ~1.5 turns.
            const turns = 1.5;
            for (let i = 0; i < n; i += 1) {
                const f = i / (n - 1);
                out.push(at(R * f, f * turns * Math.PI * 2));
            }
            break;
        }
        case "sunflower":
        default: {
            // Phyllotaxis: the i-th point sits at angle i·137.5° and radius
            // √(i/(n-1))·R, filling the disk evenly with no clumping.
            const golden = Math.PI * (3 - Math.sqrt(5));
            for (let i = 0; i < n; i += 1) {
                out.push(at(R * Math.sqrt(i / (n - 1)), i * golden));
            }
            break;
        }
    }
    return out;
}

/**
 * Produce the label for a copy of `label`, given the labels already in use.
 *
 * When the source label ends in a number (e.g. "Node 1", "Step 02"), the copy
 * gets the next free number sharing the same prefix — "Node 1" → "Node 2", and
 * if "Node 2" is taken too, "Node 3", and so on. Any zero-padding width is
 * preserved ("Step 02" → "Step 03"). Labels without a trailing number are
 * returned unchanged (matching prior copy/duplicate behavior). `existingLabels`
 * should include every current label so the copy never collides with one.
 */
export function nextCopyLabel(
    label: string,
    existingLabels: Iterable<string>,
): string {
    const match = /^(.*?)(\d+)(\s*)$/.exec(label);
    if (!match) return label;
    const [, prefix, digits, trailing] = match;
    const width = digits!.length;
    const taken = new Set(existingLabels);
    let n = parseInt(digits!, 10) + 1;
    let candidate = `${prefix}${String(n).padStart(width, "0")}${trailing}`;
    while (taken.has(candidate)) {
        n += 1;
        candidate = `${prefix}${String(n).padStart(width, "0")}${trailing}`;
    }
    return candidate;
}

export const DEFAULT_ELLIPSE_RADIUS = 40;

export function createEllipse(
    center: Vec2,
    rx: number,
    ry: number,
    index: number,
    layerId: string,
): DiagramEllipse {
    return {
        id: newId("ellipse"),
        label: `Ellipse ${index + 1}`,
        layerId,
        center: { ...center },
        rx: Math.max(0, rx),
        ry: Math.max(0, ry),
        color: "#7ca7ff",
        strokeWidth: 2,
        dash: "",
        fill: null,
        visible: true,
        startAngle: 0,
        sweep: 1,
        rotation: 0,
    };
}

export function createLine(
    start: Vec2,
    end: Vec2,
    index: number,
    layerId: string,
): DiagramLine {
    return {
        id: newId("line"),
        label: `Line ${index + 1}`,
        layerId,
        start: { ...start },
        end: { ...end },
        color: "#7ca7ff",
        strokeWidth: 2,
        dash: "",
        visible: true,
    };
}

export function createArc(
    start: Vec2,
    end: Vec2,
    index: number,
    layerId: string,
): DiagramArc {
    return {
        id: newId("arc"),
        label: `Arc ${index + 1}`,
        layerId,
        start: { ...start },
        end: { ...end },
        color: "#7ca7ff",
        strokeWidth: 2,
        dash: "",
        visible: true,
        flip: false,
        sweep: 0.5,
    };
}

export function createRect(
    start: Vec2,
    end: Vec2,
    index: number,
    layerId: string,
): DiagramRect {
    return {
        id: newId("rect"),
        label: `Rect ${index + 1}`,
        layerId,
        start: { ...start },
        end: { ...end },
        color: "#7ca7ff",
        strokeWidth: 2,
        dash: "",
        visible: true,
        fill: null,
        cornerRadius: 0,
        cornerStyle: "round",
        rotation: 0,
    };
}

export function createPolygon(
    points: Vec2[],
    index: number,
    layerId: string,
): DiagramPolygon {
    return {
        id: newId("polygon"),
        label: `Polygon ${index + 1}`,
        layerId,
        points: points.map((p) => ({ ...p })),
        color: "#7ca7ff",
        strokeWidth: 2,
        dash: "",
        fill: null,
        visible: true,
        cornerRadius: 0,
        cornerStyle: "round",
    };
}

export const DEFAULT_HEAT_ZONE_COLOR = "#ff5a3c";
export const DEFAULT_HEAT_ZONE_RADIUS = 120;

export function createHeatZone(
    start: Vec2,
    end: Vec2,
    index: number,
    layerId: string,
): DiagramHeatZone {
    return {
        id: newId("heatzone"),
        label: `Heat zone ${index + 1}`,
        layerId,
        start: { ...start },
        end: { ...end },
        color: DEFAULT_HEAT_ZONE_COLOR,
        radius: DEFAULT_HEAT_ZONE_RADIUS,
        visible: true,
    };
}


export function createStrokeStyle(index = 0): StrokeStyle {
    const palette = ["#a0c4ff", "#ffadad", "#caffbf", "#ffd6a5", "#bdb2ff"];
    return {
        id: newId("strokestyle"),
        name: `Stroke ${index + 1}`,
        color: palette[index % palette.length] ?? "#a0c4ff",
        strokeWidth: 2,
        dash: "",
        lineCount: 1,
        lineSpacing: 6,
        renderKind: "stroke",
        objectSvgs: [],
        objectSpacing: 24,
        objectScale: 16,
        objectOrder: "sequence",
        objectRotation: "align",
        objectAngleDeg: 0,
        heatReactive: false,
    };
}

export function createDefaultPreset(
    index = 0,
    strokeStyleId = "",
): ConnectionPreset {
    return {
        id: newId("preset"),
        name: `Connection ${index + 1}`,
        strokeStyleId,
        routing: "straight",
        bendCount: 1,
        cornerRadius: 0,
        cornerStyle: "round",
        arcFlip: false,
        arcSpacedEnds: false,
        arcEndsAlongChord: false,
        arcSweep: 0.5,
        sineAmplitude: 16,
        sineWavelength: 48,
        bridge: false,
    };
}

export function createGroup(
    nodeIds: string[],
    index: number,
    layerId: string,
    strokeStyles: StrokeStyle[] = [],
): NodeGroup {
    return {
        id: newId("group"),
        name: `Group ${index + 1}`,
        layerId,
        nodeIds: [...nodeIds],
        // Groups get an outline by default, styled with the first stroke style
        // preset when one exists (falling back to the plain default stroke).
        properties: [createOutline(strokeStyles[0]?.id ?? null)],
    };
}

export function createRadialEmitterEffect(
    index: number,
    strokeStyleId: string | null,
): RadialEmitterEffect {
    return {
        id: newId("effect"),
        name: `Effect ${index + 1}`,
        kind: "radial-emitter",
        rayCount: 8,
        innerRadius: 14,
        outerRadius: 40,
        strokeStyleId,
    };
}

export function createConcentricRingsEffect(
    index: number,
    strokeStyleId: string | null,
): ConcentricRingsEffect {
    return {
        id: newId("effect"),
        name: `Effect ${index + 1}`,
        kind: "concentric-rings",
        ringCount: 3,
        innerRadius: 18,
        spacing: 12,
        strokeStyleId,
    };
}

/** Create a new effect of the given kind with sensible defaults. */
export function createEffect(
    kind: EffectKind,
    index: number,
    strokeStyleId: string | null,
): Effect {
    switch (kind) {
        case "concentric-rings":
            return createConcentricRingsEffect(index, strokeStyleId);
        case "radial-emitter":
        default:
            return createRadialEmitterEffect(index, strokeStyleId);
    }
}

export function createSquareSpawnPattern(templateId: string): SquareSpawnPattern {
    return { kind: "square", templateId, countPerSide: 3, gap: 20 };
}

export function createCircleSpawnPattern(templateId: string): CircleSpawnPattern {
    return { kind: "circle", templateId, count: 8, radius: 60 };
}

export function createSpawnPattern(
    kind: SpawnPatternKind,
    templateId: string,
): SpawnPattern {
    return kind === "circle"
        ? createCircleSpawnPattern(templateId)
        : createSquareSpawnPattern(templateId);
}

export function createOutline(strokeStyleId: string | null): OutlineProperty {
    return {
        kind: "outline",
        padding: 16,
        strokeStyleId,
        closed: true,
        shape: "hull",
        bendCount: 4,
        cornerRadius: 0,
        cornerStyle: "round",
        fill: null,
    };
}

/** A new reusable group-outline preset, seeded from the default outline. */
export function createGroupOutlinePreset(
    index: number,
    strokeStyleId: string | null,
): GroupOutlinePreset {
    const { kind: _kind, presetId: _presetId, ...fields } =
        createOutline(strokeStyleId);
    return {
        id: newId("group-outline-preset"),
        name: `Outline preset ${index + 1}`,
        ...fields,
    };
}

/**
 * Registry mapping group-property kinds to default factories. Centralizes
 * creation so the context/controls don't hard-code shapes and new kinds plug
 * in one place. (Node decorations live in the Effects system instead.)
 */
export const PROPERTY_REGISTRY = {
    group: {
        outline: { label: "Outline", create: createOutline },
    },
} as const;

/** A new tiled-SVG pattern seeded with one object, with sensible default tiling. */
export function createPattern(
    name: string,
    svg: string,
    index: number,
): DiagramPattern {
    return {
        id: newId("pattern"),
        name: name || `Pattern ${index + 1}`,
        objects: [{ id: newId("patobj"), name: name || "Object", svg }],
        order: "sequence",
        tileSize: 24,
        spacingX: 0,
        spacingY: 0,
        rotationDeg: 0,
        backgroundColor: null,
    };
}

export function createEmptyDocument(): DiagramDocument {
    const strokeStyle = createStrokeStyle(0);
    return {
        layers: [createLayer(0)],
        nodes: [],
        connections: [],
        strokeStyles: [strokeStyle],
        presets: [createDefaultPreset(0, strokeStyle.id)],
        effects: [],
        patterns: [],
        groups: [],
        groupOutlinePresets: [],
        ellipses: [],
        lines: [],
        arcs: [],
        rects: [],
        polygons: [],
        heatZones: [],
        symmetry: { x: false, y: false, guideX: false, guideY: false },
    };
}
// --- cloning (undo/redo + persistence) ------------------------------------

export function cloneDocument(doc: DiagramDocument): DiagramDocument {
    return structuredClone(doc);
}

/**
 * Merge a connection routing preset with its referenced stroke style. Falls back
 * to the first available stroke style when the preset's `strokeStyleId` is
 * missing (so a freshly loaded/edited doc never renders an unstyled connection),
 * then to default stroke fields when no styles exist at all.
 */
export function resolveConnectionStyle(
    preset: ConnectionPreset,
    strokeStyles: StrokeStyle[],
): ResolvedConnectionStyle {
    const style =
        strokeStyles.find((s) => s.id === preset.strokeStyleId) ??
        strokeStyles[0] ??
        createStrokeStyle(0);
    const { id: _id, name: _name, ...strokeFields } = style;
    const { strokeStyleId: _strokeStyleId, ...routingFields } = preset;
    return { ...routingFields, ...strokeFields };
}

